import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, Circle, Stethoscope, Shield, Pill, Activity, Loader2,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { useEncounter } from '@/contexts/EncounterContext';
import { emrService } from '@/services/emrService';
import { allergiesService } from '@/services/allergiesService';
import { medicationsService } from '@/services/medicationsService';
import { vitalsService, VitalLoinc } from '@/services/vitalsService';
import { parseApiError } from '@/lib/apiErrors';
import ReconcileAllergiesDialog from './ReconcileAllergiesDialog';
import ReconcileMedicationsDialog from './ReconcileMedicationsDialog';
import VitalsCaptureSheet from '../emr/VitalsCaptureSheet';
import { cacheKeys, cacheRoots } from '@/lib/cacheKeys';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';
const CC_AUTOSAVE_MS = 800;

/**
 * Rooming as a passive checklist inside the cockpit.
 *
 * Replaces the old `StartVisitDrawer` blocking flow. The provider can jump
 * straight into documentation and tick off rooming items when they're
 * actually doing them — chief complaint can be captured during the
 * conversation, vitals when an MA enters them, etc.
 *
 * Each item is "complete" purely from data (chief complaint set, latest
 * reconciliation event present, latest vitals fresh enough). Nothing is
 * gated — this is decision support, not a wizard.
 */
const RoomingChecklistPanel: React.FC = () => {
  const { clinicId, encounterId, patientId, encounter, isSigned } = useEncounter();
  const qc = useQueryClient();

  const [chiefComplaint, setChiefComplaint] = useState(encounter?.chief_complaint || '');
  const [ccSaveState, setCcSaveState] = useState<SaveState>('idle');
  const ccTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedCcRef = useRef<string>(encounter?.chief_complaint || '');
  const [showAllergies, setShowAllergies] = useState(false);
  const [showMeds, setShowMeds] = useState(false);
  const [showVitals, setShowVitals] = useState(false);

  // Allergies completeness: any active allergy with verification_status=unconfirmed
  // means reconciliation is still pending.
  const { data: allergies = [] } = useQuery({
    queryKey: cacheKeys.patientAllergies(patientId, { activeOnly: true }),
    queryFn: () => allergiesService.getPatientAllergies(clinicId, patientId, { activeOnly: true }),
  });
  const allergiesUnconfirmed = allergies.some(a => a.verification_status === 'unconfirmed');

  // Medications completeness: any med whose last_reconciled_at is missing or
  // older than this encounter's start counts as un-reconciled.
  const { data: meds = [] } = useQuery({
    queryKey: cacheKeys.patientMedications(patientId, { activeOnly: true }),
    queryFn: () => medicationsService.getPatientMedications(clinicId, patientId, { activeOnly: true }),
  });
  const encounterStartMs = encounter?.start_time ? new Date(encounter.start_time).getTime() : 0;
  const medsUnreconciled = meds.length > 0 && meds.some(m =>
    !m.last_reconciled_at || new Date(m.last_reconciled_at).getTime() < encounterStartMs,
  );

  // Vitals completeness: any vital observation recorded for this encounter
  // counts as "captured".
  const { data: encounterVitals = [] } = useQuery({
    queryKey: cacheKeys.encounterVitals(encounterId),
    queryFn: () => vitalsService.getEncounterVitals(clinicId, encounterId),
  });
  const hasEncounterVitals = encounterVitals.length > 0;

  const items = [
    {
      key: 'chief',
      label: 'Chief complaint',
      icon: Stethoscope,
      done: !!chiefComplaint.trim(),
      pending: false,
    },
    {
      key: 'allergies',
      label: 'Reconcile allergies',
      icon: Shield,
      done: allergies.length === 0 || !allergiesUnconfirmed,
      pending: allergiesUnconfirmed,
    },
    {
      key: 'meds',
      label: 'Reconcile medications',
      icon: Pill,
      done: meds.length === 0 || !medsUnreconciled,
      pending: medsUnreconciled,
    },
    {
      key: 'vitals',
      label: 'Capture vitals',
      icon: Activity,
      done: hasEncounterVitals,
      pending: false,
    },
  ];

  const completedCount = items.filter(i => i.done).length;
  const total = items.length;

  // Debounced autosave for chief complaint. No explicit Save button — typing
  // pauses for ~800ms and the change persists. Failures fall back to a toast
  // and the inline status flips to "error" so the practitioner can retry by
  // editing again.
  useEffect(() => {
    if (isSigned) return;
    const trimmed = chiefComplaint.trim();
    if (trimmed === lastSavedCcRef.current.trim()) {
      setCcSaveState('idle');
      return;
    }
    if (ccTimerRef.current) clearTimeout(ccTimerRef.current);
    ccTimerRef.current = setTimeout(async () => {
      setCcSaveState('saving');
      try {
        await emrService.updateEncounter(clinicId, encounterId, {
          chief_complaint: trimmed,
        });
        lastSavedCcRef.current = trimmed;
        setCcSaveState('saved');
        qc.invalidateQueries({ queryKey: cacheKeys.encounter(encounterId) });
      } catch (err) {
        setCcSaveState('error');
        const e = parseApiError(err);
        toast({ title: 'Chief complaint not saved', description: e.message, variant: 'destructive' });
      }
    }, CC_AUTOSAVE_MS);
    return () => {
      if (ccTimerRef.current) clearTimeout(ccTimerRef.current);
    };
  }, [chiefComplaint, clinicId, encounterId, isSigned, qc]);

  return (
    <>
      <Card>
        <CardHeader className='flex-row items-center justify-between space-y-0'>
          <CardTitle className='text-base flex items-center gap-2'>
            Rooming checklist
          </CardTitle>
          <Badge
            variant='outline'
            className={completedCount === total
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-amber-200 bg-amber-50 text-amber-700'
            }
          >
            {completedCount}/{total} complete
          </Badge>
        </CardHeader>

        <CardContent className='space-y-4'>
          <p className='text-xs text-slate-500'>
            Rooming items are tracked but not required. Document the visit at your own pace —
            check items off when they happen.
          </p>

          {/* Chief complaint inline editor — autosaves on typing pause. */}
          <div className='rounded-md border border-slate-200 bg-white p-3'>
            <div className='mb-2 flex items-center justify-between gap-2'>
              <div className='flex items-center gap-2'>
                <ChecklistIcon done={items[0].done} />
                <Label htmlFor='cc' className='text-sm font-medium'>{items[0].label}</Label>
              </div>
              <CcSaveStatus state={ccSaveState} />
            </div>
            <Textarea
              id='cc'
              value={chiefComplaint}
              onChange={e => setChiefComplaint(e.target.value)}
              placeholder='Why is the patient here today?'
              className='min-h-[80px]'
              disabled={isSigned}
            />
          </div>

          {/* Reconcile allergies */}
          <ChecklistRow
            done={items[1].done}
            pending={items[1].pending}
            label={items[1].label}
            icon={items[1].icon}
            description={allergies.length === 0
              ? 'No active allergies on file (NKDA).'
              : allergiesUnconfirmed
                ? `${allergies.filter(a => a.verification_status === 'unconfirmed').length} unconfirmed entry(ies). Confirm or remove each one.`
                : `${allergies.length} active allergy(ies), all confirmed.`
            }
            actionLabel={allergiesUnconfirmed ? 'Reconcile' : 'Review'}
            onAction={() => setShowAllergies(true)}
            disabled={isSigned}
          />

          {/* Reconcile medications */}
          <ChecklistRow
            done={items[2].done}
            pending={items[2].pending}
            label={items[2].label}
            icon={items[2].icon}
            description={meds.length === 0
              ? 'No active medications on file.'
              : medsUnreconciled
                ? `${meds.length} active med(s) — review and confirm.`
                : `${meds.length} active med(s), reconciled this visit.`
            }
            actionLabel={medsUnreconciled ? 'Reconcile' : 'Review'}
            onAction={() => setShowMeds(true)}
            disabled={isSigned}
          />

          {/* Capture vitals */}
          <ChecklistRow
            done={items[3].done}
            pending={false}
            label={items[3].label}
            icon={items[3].icon}
            description={hasEncounterVitals
              ? `${encounterVitals.length} vital(s) recorded for this visit.`
              : 'No vitals captured for this visit yet.'
            }
            actionLabel={hasEncounterVitals ? 'Add more' : 'Capture'}
            onAction={() => setShowVitals(true)}
            disabled={isSigned}
          />
        </CardContent>
      </Card>

      <ReconcileAllergiesDialog
        open={showAllergies}
        onOpenChange={setShowAllergies}
        clinicId={clinicId}
        patientId={patientId}
        encounterId={encounterId}
        onCompleted={() => {
          qc.invalidateQueries({ queryKey: cacheRoots.patientAllergies(patientId) });
        }}
      />
      <ReconcileMedicationsDialog
        open={showMeds}
        onOpenChange={setShowMeds}
        clinicId={clinicId}
        patientId={patientId}
        encounterId={encounterId}
        onCompleted={() => {
          qc.invalidateQueries({ queryKey: cacheRoots.patientMedications(patientId) });
        }}
      />
      <VitalsCaptureSheet
        open={showVitals}
        onOpenChange={setShowVitals}
        clinicId={clinicId}
        encounterId={encounterId}
        patientId={patientId}
        onRecorded={() => setShowVitals(false)}
      />
    </>
  );
};

const ChecklistIcon: React.FC<{ done: boolean; pending?: boolean }> = ({ done, pending }) => {
  if (done) return <CheckCircle2 className='h-4 w-4 text-emerald-600' />;
  if (pending) return <AlertCircle className='h-4 w-4 text-amber-600' />;
  return <Circle className='h-4 w-4 text-slate-400' />;
};

const CcSaveStatus: React.FC<{ state: SaveState }> = ({ state }) => {
  if (state === 'saving') {
    return (
      <span className='inline-flex items-center gap-1 text-[11px] text-slate-500'>
        <Loader2 className='h-3 w-3 animate-spin' /> Saving…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className='inline-flex items-center gap-1 text-[11px] text-emerald-600'>
        <CheckCircle2 className='h-3 w-3' /> Saved
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className='inline-flex items-center gap-1 text-[11px] text-rose-600'>
        <AlertCircle className='h-3 w-3' /> Not saved
      </span>
    );
  }
  return null;
};

const ChecklistRow: React.FC<{
  done: boolean;
  pending: boolean;
  label: string;
  icon: React.ElementType;
  description: string;
  actionLabel: string;
  onAction: () => void;
  disabled?: boolean;
}> = ({ done, pending, label, icon: Icon, description, actionLabel, onAction, disabled }) => (
  <div className='rounded-md border border-slate-200 bg-white p-3'>
    <div className='flex items-start justify-between gap-3'>
      <div className='flex items-start gap-2 min-w-0'>
        <ChecklistIcon done={done} pending={pending} />
        <div className='min-w-0'>
          <div className='flex items-center gap-2'>
            <Icon className='h-3.5 w-3.5 text-slate-500' />
            <span className='text-sm font-medium'>{label}</span>
          </div>
          <p className='mt-0.5 text-xs text-slate-500'>{description}</p>
        </div>
      </div>
      {!disabled && (
        <Button size='sm' variant='outline' onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  </div>
);

export default RoomingChecklistPanel;
